import { Box, Button, Divider, Tooltip } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { PageHeader } from 'src/components/molecules/page-header';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { fetchAdmins } from 'src/features/administrators/tenant/api/lib/admins';
import { adminsKeys } from 'src/features/administrators/tenant/api/query-keys/admins';
import { AdminToManage } from 'src/features/administrators/tenant/types/payloads/admin-to-manage';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { UserRole } from 'src/types/enum/user-role';
import AdminsActionsTableMenu from '../molecules/admins-actions-table-menu';
import AdminsTable from '../organisms/admins-table';
import InviteAdminDialog from '../organisms/dialogs/invite-admin-dialog';
import RemoveAdminDialog from '../organisms/dialogs/remove-admin-dialog';
import ResendInvitationDialog from '../organisms/dialogs/resend-invitation-dialog';
import UpdateAdminPhoneNumberDialog from '../organisms/dialogs/update-admin-phone-number-dialog';
import UpdateAdminRoleDialog from '../organisms/dialogs/update-admin-role-dialog';

const Admins = () => {
  const { t } = useTranslation();

  const { adminToken, directoryToken } = useUserStore();

  const { query, perPage, page } = useSearch({
    from: '/_layout/tenant/organization-details/_layout/admins',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'email',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeSort,
  } = useMeilisearchWithUrlParams(
    fetchAdmins,
    adminsKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  const [
    isOpenInviteAdminDialog,
    handleOpenInviteAdminDialog,
    handleCloseInviteAdminDialog,
  ] = useDialog();

  const [
    isOpenRemoveAdminDialog,
    handleOpenRemoveAdminDialog,
    handleCloseRemoveAdminDialog,
  ] = useDialog();

  const [
    isOpenUpdateAdminPhoneNumberDialog,
    handleOpenUpdateAdminPhoneNumberDialog,
    handleCloseUpdateAdminPhoneNumberDialog,
  ] = useDialog();

  const [
    isOpenUpdateAdminRoleDialog,
    handleOpenUpdateAdminRoleDialog,
    handleCloseUpdateAdminRoleDialog,
  ] = useDialog();

  const [
    isOpenResendAdminInvitationDialog,
    handleOpenResendAdminInvitationDialog,
    handleCloseResendAdminInvitationDialog,
  ] = useDialog();

  const [adminToManage, setAdminToManage] = useState<AdminToManage | null>(
    null,
  );

  const actionsSlot = useCallback(
    ({ token, email, phoneNumber, role, status }: AdminToManage) => (
      <AdminsActionsTableMenu
        adminToken={token}
        adminRole={role}
        isCurrentAdminSelfIdentityEqual={adminToken === token}
        onEditPhoneNumberActionClick={() => {
          handleOpenUpdateAdminPhoneNumberDialog();
          setAdminToManage({ token, email, phoneNumber, role, status });
        }}
        onEditRoleActionClick={() => {
          handleOpenUpdateAdminRoleDialog();
          setAdminToManage({ token, email, phoneNumber, role, status });
        }}
        onRemoveAdminActionClick={() => {
          handleOpenRemoveAdminDialog();
          setAdminToManage({ token, email, phoneNumber, role, status });
        }}
        onResendInvitationActionClick={() => {
          handleOpenResendAdminInvitationDialog();
          setAdminToManage({ token, email, phoneNumber, role, status });
        }}
        invitationStatus={status}
      />
    ),
    [],
  );

  return (
    <>
      <PageHeader
        title={t('organizationDetails:admins.title')}
        extras={
          <Can I="create" a="TenantAdministrators" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}>
                <span>
                  <Button
                    onClick={handleOpenInviteAdminDialog}
                    color="primary"
                    startIcon={<Plus fontSize="small" />}
                    variant="contained"
                    size="large"
                    disabled={!allowed}>
                    {t('organizationDetails:admins.inviteButton')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        }
      />
      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />
        </Box>

        <AdminsTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message ?? ''}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>

      <InviteAdminDialog
        open={isOpenInviteAdminDialog}
        onCancel={handleCloseInviteAdminDialog}
        adminToken={adminToManage?.token ?? ''}
        adminRole={adminToManage?.role ?? UserRole.VIEWER}
      />
      <RemoveAdminDialog
        open={isOpenRemoveAdminDialog}
        onCancel={handleCloseRemoveAdminDialog}
        adminToken={adminToManage?.token ?? ''}
        adminEmail={adminToManage?.email ?? ''}
      />
      <UpdateAdminPhoneNumberDialog
        open={isOpenUpdateAdminPhoneNumberDialog}
        onCancel={handleCloseUpdateAdminPhoneNumberDialog}
        adminToken={adminToManage?.token ?? ''}
        adminPhoneNumber={adminToManage?.phoneNumber ?? ''}
      />
      <UpdateAdminRoleDialog
        open={isOpenUpdateAdminRoleDialog}
        onCancel={handleCloseUpdateAdminRoleDialog}
        adminToken={adminToManage?.token ?? ''}
        adminRole={adminToManage?.role ?? UserRole.VIEWER}
      />
      <ResendInvitationDialog
        open={isOpenResendAdminInvitationDialog}
        onCancel={handleCloseResendAdminInvitationDialog}
        email={adminToManage?.email ?? ''}
        token={adminToManage?.token ?? ''}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'TenantAdministrators'),
  Admins,
);
